import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { TONE_CHIP, type Tone } from "../../domain/status";
import { Loading } from "./Spinner";

export function IconChip({ icon, tone = "primary", size = 48, className }: { icon: ReactNode; tone?: Tone; size?: number; className?: string }) {
  return (
    <span aria-hidden="true" className={cn("inline-flex shrink-0 items-center justify-center rounded-[14px] [&>svg]:h-[45%] [&>svg]:w-[45%]", TONE_CHIP[tone], className)} style={{ width: size, height: size }}>
      {icon}
    </span>
  );
}

/** Table empty state: a muted icon plus one line, inside the table body (§2.2). */
export function TableEmpty({ colSpan, icon, children }: { colSpan: number; icon: ReactNode; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="!py-12 text-center">
        <div className="flex flex-col items-center gap-2.5 text-neutral-500">
          <span className="text-neutral-300 [&>svg]:h-8 [&>svg]:w-8" aria-hidden="true">
            {icon}
          </span>
          <div className="text-base font-medium">{children}</div>
        </div>
      </td>
    </tr>
  );
}

export function SkeletonRows({ rows = 6, cols }: { rows?: number; cols: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true">
          {Array.from({ length: cols }, (_x, c) => (
            <td key={c}>
              <div className="skeleton h-3.5" style={{ width: `${45 + ((r * 7 + c * 13) % 45)}%` }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/** A page whose whole content is one card or message: centred both ways in the content
 * area, never pinned top-left (§2.2). */
export function Centered({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("flex min-h-full w-full flex-1 items-center justify-center p-6", className)}>{children}</div>;
}

/** Full-page empty / error: centred tinted icon chip + one sentence (§3.3). */
export function CenteredState({ icon, tone = "neutral", title, children, action }: { icon: ReactNode; tone?: Tone; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <Centered>
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <IconChip icon={icon} tone={tone} size={56} />
        {title && <h2 className="card-title">{title}</h2>}
        {children && <p className="text-base text-neutral-600">{children}</p>}
        {action}
      </div>
    </Centered>
  );
}

export function CenteredLoading() {
  return (
    <Centered>
      <Loading />
    </Centered>
  );
}
